import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { UseMutationResult, useMutation } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import {
  useAgentExecutionClient,
  useAgentSession,
} from "src/agents/queries/execution";
import type {
  AgentInvokeResponse,
  AgentPlaygroundInvokeRequest,
  SessionStatus,
  TerminationReason,
} from "src/clients/agent-management";

const PREWARM_COOLDOWN_MS = 20_000; // dedup window in ms

/**
 * Module-level storage for playground IDs.
 * - Fresh ID generated on each page load
 * - Automatically reset on page refresh
 * - Isolated per browser tab
 */
const playgroundIdMap = new Map<string, string>();

/**
 * Module-level ping promise tracking.
 * Tracks in-flight ping requests per agent/version to enable invoke queueing.
 * When a ping is in-flight, invoke requests wait for it to complete
 * to avoid concurrent requests to the same container session.
 */
const pingPromiseMap = new Map<string, Promise<void>>();

/**
 * Subscription infrastructure for ping state.
 * Allows React components to subscribe to ping state changes via useSyncExternalStore.
 */
const pingListeners = new Set<() => void>();

const subscribeToPingState = (callback: () => void): (() => void) => {
  pingListeners.add(callback);
  return () => pingListeners.delete(callback);
};

const notifyPingListeners = (): void => {
  pingListeners.forEach((cb) => cb());
};

const getOrCreatePlaygroundId = (
  agentId: string,
  versionId: string,
): string => {
  const key = `${agentId}-${versionId}`;
  let id = playgroundIdMap.get(key);
  if (!id) {
    id = uuidv4();
    playgroundIdMap.set(key, id);
  }
  return id;
};

const resetPlaygroundId = (agentId: string, versionId: string): string => {
  const key = `${agentId}-${versionId}`;
  const newId = uuidv4();
  playgroundIdMap.set(key, newId);
  return newId;
};

type PlaygroundInvokeParams = {
  invocationMessage: string;
  structuredInput?: Record<string, unknown>;
};

/**
 * Frontend-only session status that extends the backend SessionStatus
 * with "booting" for when the container is warming up.
 */
export type PlaygroundSessionStatus = SessionStatus | "booting";

type PlaygroundContextValue = {
  playgroundId: string;
  /**
   * Current backend server session ID from the most recent invocation.
   * Null when no invocation has been made or after reset.
   */
  currentSessionId: string | null;
  /**
   * Computed session status including frontend-only states.
   * - Returns "booting" when ping is in-flight (container warming up)
   * - Returns "running" optimistically when invoke is pending
   * - Returns actual server status once available
   * - Returns null when no session
   */
  sessionStatus: PlaygroundSessionStatus | null;
  /**
   * Termination reason if session failed.
   */
  terminationReason: TerminationReason | null;
  /**
   * Termination message providing additional details if session failed.
   */
  terminationMessage: string | null;
  ping: () => void;
  /**
   * Resets the playground session.
   * Automatically reuses the playground ID if unused or session completed,
   * generates a new one only if a session is in-progress.
   */
  resetSession: () => void;
  invoke: UseMutationResult<AgentInvokeResponse, Error, PlaygroundInvokeParams>;
};

const PlaygroundContext = createContext<PlaygroundContextValue | null>(null);

type PlaygroundProviderProps = {
  agentId: string;
  versionId: string;
  children: React.ReactNode;
};

/**
 * Provides playground session state to children.
 *
 * - Fresh playground ID generated on each page load
 * - Isolated per browser tab
 * - Only regenerates on explicit resetSession() call or page refresh
 * - Provides ping() that sends the playground ID with prewarm requests
 * - Uses a cooldown window to deduplicate rapid ping triggers
 * - Tracks in-flight pings to enable invoke queueing (prevents 502 errors from agent runner)
 * - Exposes frontend session status for UI display (e.g. "Booting..." vs "Thinking...")
 */
export const PlaygroundProvider: React.FC<PlaygroundProviderProps> = ({
  agentId,
  versionId,
  children,
}) => {
  const client = useAgentExecutionClient();
  const lastPingRef = useRef<number>(0);
  const key = `${agentId}-${versionId}`;

  // Track ping in-flight state for UI via external store subscription.
  // We use useSyncExternalStore instead of useState to avoid stale setter issues:
  // if user navigates away while ping is in-flight, the .finally() callback would
  // call the unmounted component's setter, leaving new component's state stale.
  const isPingInFlight = useSyncExternalStore(
    subscribeToPingState,
    () => pingPromiseMap.has(key),
    () => false,
  );

  const firePing = useCallback(
    (targetPlaygroundId: string) => {
      if (!client) return;

      lastPingRef.current = Date.now();

      const pingPromise = client
        .playgroundPingAgentsManagementApiV0PlaygroundPingPost({
          playgroundPingRequest: {
            agent_id: agentId,
            agent_version_id: versionId,
            playground_id: targetPlaygroundId,
          },
        })
        .catch(() => {}) // Swallow errors
        .finally(() => {
          if (pingPromiseMap.get(key) !== pingPromise) {
            // Outdated/stale promise, don't clean up the newer one
            return;
          }
          pingPromiseMap.delete(key);
          notifyPingListeners();
        }) as unknown as Promise<void>;

      pingPromiseMap.set(key, pingPromise);
      notifyPingListeners();
    },
    [client, agentId, versionId, key],
  );

  // Track current session ID for status polling
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Track if invoke is pending (for optimistic "running" status before we have sessionId)
  const [isInvokePending, setIsInvokePending] = useState<boolean>(false);

  // Module-level storage ensures all components share the same ID
  // Fresh ID on page load, stable within session
  const [playgroundId, setPlaygroundId] = useState<string>(() =>
    getOrCreatePlaygroundId(agentId, versionId),
  );

  // Query session status for the current session
  const sessionQuery = useAgentSession(currentSessionId);

  // Compute session status with frontend-only states
  const serverStatus = sessionQuery.data?.status ?? null;
  const isWaitingForFirstPoll = currentSessionId && !serverStatus;
  const isOptimisticallyRunning = isInvokePending || isWaitingForFirstPoll;

  let sessionStatus: PlaygroundSessionStatus | null;
  if (serverStatus) {
    // If we have a real status from the backend, always use it
    // (don't let ping override an active session's status)
    sessionStatus = serverStatus;
  } else if (isPingInFlight) {
    // Only show "booting" when no session status exists yet
    sessionStatus = "booting";
  } else if (isOptimisticallyRunning) {
    sessionStatus = "running";
  } else {
    sessionStatus = null;
  }

  const terminationReason = sessionQuery.data?.termination_reason ?? null;
  const terminationMessage = sessionQuery.data?.termination_message ?? null;
  const isSessionComplete =
    serverStatus === "success" ||
    serverStatus === "failed" ||
    serverStatus === "cancelled";

  const ping = useCallback(() => {
    // Skip if ping already in-flight
    if (pingPromiseMap.has(key)) {
      return;
    }

    // Dedupe: skip if pinged within cooldown window
    if (Date.now() - lastPingRef.current < PREWARM_COOLDOWN_MS) {
      return;
    }

    firePing(playgroundId);
  }, [key, playgroundId, firePing]);

  const resetSession = useCallback(() => {
    // Reuse playground ID if:
    // - Never used (no session started), OR
    // - Session completed (container is warm and ready)
    // Generate new ID only if session is in-progress
    const shouldReusePlaygroundId =
      currentSessionId === null || isSessionComplete;

    // Clear session state
    setCurrentSessionId(null);
    setIsInvokePending(false);

    if (shouldReusePlaygroundId) {
      // Reusing playground ID - use ping() which respects cooldown
      ping();
    } else {
      // New playground ID - generate and ping immediately
      const newPlaygroundId = resetPlaygroundId(agentId, versionId);
      setPlaygroundId(newPlaygroundId);
      firePing(newPlaygroundId);
    }
  }, [agentId, versionId, currentSessionId, isSessionComplete, ping, firePing]);

  /**
   * Waits for any in-flight ping to complete before resolving.
   * Used by invoke to ensure sequential requests to AgentCore during coldstarts.
   */
  const waitForPingIfAny = useCallback(async (): Promise<void> => {
    const currentPingPromise = pingPromiseMap.get(key);
    if (currentPingPromise) {
      await currentPingPromise;
    }
  }, [key]);

  // Invoke mutation - internally awaits ping before firing
  const invoke = useMutation({
    mutationFn: async (
      params: PlaygroundInvokeParams,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }

      // Wait for any in-flight ping to complete first
      await waitForPingIfAny();

      const requestData: AgentPlaygroundInvokeRequest = {
        agent_id: agentId,
        agent_version_id: versionId,
        invocation_message: params.invocationMessage,
        playground_id: playgroundId,
      };

      // Only add structured_input if it has at least 1 field
      if (
        params.structuredInput &&
        Object.keys(params.structuredInput).length > 0
      ) {
        requestData.structured_input = params.structuredInput;
      }

      const response =
        await client.playgroundInvokeAgentAgentsManagementApiV0PlaygroundInvokePost(
          {
            agentPlaygroundInvokeRequest: requestData,
          },
        );

      // Capture session ID on success
      setCurrentSessionId(response.data.session_id);

      return response.data;
    },
    onMutate: () => {
      // Set optimistic pending state immediately when mutation starts
      setIsInvokePending(true);
    },
    onSettled: () => {
      // Clear pending state on both success and error
      setIsInvokePending(false);
    },
  });

  return (
    <PlaygroundContext.Provider
      value={{
        playgroundId,
        currentSessionId,
        sessionStatus,
        terminationReason,
        terminationMessage,
        ping,
        resetSession,
        invoke,
      }}
    >
      {children}
    </PlaygroundContext.Provider>
  );
};

/**
 * Hook to access playground session state. Must be used within a PlaygroundProvider.
 */
export const usePlaygroundContext = (): PlaygroundContextValue => {
  const context = useContext(PlaygroundContext);
  if (!context) {
    throw new Error(
      "usePlaygroundContext must be used within PlaygroundProvider",
    );
  }
  return context;
};

/**
 * Reset module-level state for testing purposes.
 */
export const __resetPlaygroundState = (): void => {
  playgroundIdMap.clear();
  pingPromiseMap.clear();
  pingListeners.clear();
};
